import { TourConfig, TourStep } from '../types';
import { END2_SPEECH_BUBBLE } from './end2';

export const GUIDED_JOURNEY_ID = 'guidedJourney';

/**
 * The guided journey config (layer C) — the authored source of truth.
 *
 * There are no pause/resume/auto-pause mechanisms: a step's `banner.when` gates it to a path,
 * and when it does not match the step's `pausedBanner` variant renders instead. Every
 * transition is a button — `targetStepId` to move on, `goBack: true` to pop the history stack
 * (the one real mechanism, needed because merge points have many possible parents).
 *
 * Edit steps here directly. See CONTEXT.md and docs/adr/0001-tour-config-vs-state-decoupling.md.
 */
export const guidedJourneySteps: TourStep[] = [
  {
    id: '0-modal',
    ui: {
      modal: {
        kind: 'welcome',
        nextStepId: 'Home 1',
      },
    },
    analytics: {
      stepNumber: 0,
    },
  },
  {
    id: 'Home 1',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "Welcome to our home base - where you can catch the latest live happenings across all the zoos! We'll check back in here so I can show you some hidden gems. This is where it all starts.",
          buttons: [
            {
              label: "Let's Get Started",
              targetStepId: 'Home 2',
            },
          ],
          when: '/home',
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-departure',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Departure',
          nextHabitat: 'Treetops',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'O1',
              navigatePath: '/h/toronto-zoo/sumatran-orangutans?tab=watch&id=699f40177c890cb0e7aec70a',
              devNavigatePath: '/h/toronto-zoo/test-habitat1?tab=watch&card=album&id=6985edd47e9d38372a13fde9',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/home',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 1,
      section: 'Departure',
      nextHabitat: 'Treetops',
    },
  },
  {
    id: 'Home 2',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "The first stop on our tour is ... Orangutans! Let's head over to the treetops and meet 4-year-old Wali. He's sort of a celebrity around here. Are you ready?",
          buttons: [
            {
              label: 'Take me to the treetops!',
              navigatePath: '/h/toronto-zoo/sumatran-orangutans?tab=watch&id=699f40177c890cb0e7aec70a',
              devNavigatePath: '/h/toronto-zoo/test-habitat1?tab=watch&card=album&id=6985edd47e9d38372a13fde9',
              targetStepId: 'O1',
            },
          ],
          when: '/home',
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-departure',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Departure',
          nextHabitat: 'Treetops',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'O1',
              navigatePath: '/h/toronto-zoo/sumatran-orangutans?tab=watch&id=699f40177c890cb0e7aec70a',
              devNavigatePath: '/h/toronto-zoo/test-habitat1?tab=watch&card=album&id=6985edd47e9d38372a13fde9',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/home',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 2,
      section: 'Departure',
      nextHabitat: 'Treetops',
    },
  },
  {
    id: 'O1',
    ui: {
      onMediaClose: {
        targetStepId: 'O2',
      },
      banner: [
        {
          type: 'guide',
          mascot: 'idle',
          when: ['/h/toronto-zoo/sumatran-orangutans', '/h/toronto-zoo/test-habitat1'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-treetops',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Treetops',
          nextHabitat: 'Grasslands',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'MW1',
              navigatePath: '/h/endangered-wolf-center/maned-wolves',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/toronto-zoo/sumatran-orangutans?tab=watch&id=699f40177c890cb0e7aec70a',
              devNavigatePath: '/h/toronto-zoo/test-habitat1?tab=watch&card=album&id=6985edd47e9d38372a13fde9',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 3,
      section: 'Treetops',
      nextHabitat: 'Grasslands',
    },
  },
  {
    id: 'O2',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "What's amazing about habitats on zoolife is not only seeing top highlights, but also tracing their day-to-day journeys, almost like we get to live alongside them.",
          buttons: [
            {
              label: 'Explore Around',
              targetStepId: 'O3',
            },
            {
              label: "See Wali's Timeline",
              navigatePath: '/h/toronto-zoo/sumatran-orangutans?tab=watch&card=album',
              devNavigatePath: '/h/toronto-zoo/test-habitat1?tab=watch&card=album',
              targetStepId: 'O3',
            },
          ],
          when: ['/h/toronto-zoo/sumatran-orangutans', '/h/toronto-zoo/test-habitat1'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-treetops',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Treetops',
          nextHabitat: 'Grasslands',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'MW1',
              navigatePath: '/h/endangered-wolf-center/maned-wolves',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/toronto-zoo/sumatran-orangutans',
              devNavigatePath: '/h/toronto-zoo/test-habitat1',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 4,
      section: 'Treetops',
      nextHabitat: 'Grasslands',
    },
  },
  {
    id: 'O3',
    ui: {
      autoAdvance: {
        delayMs: 8000,
        targetStepId: 'O4',
        when: ['/h/toronto-zoo/sumatran-orangutans', '/h/toronto-zoo/test-habitat1'],
      },
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            'You can keep getting to know Wali here in his habitat! Try giving a 💛 to the videos his fans have clipped! Everything you save goes in "My Journal" as your personal souvenir from today\'s visit.',
          when: ['/h/toronto-zoo/sumatran-orangutans', '/h/toronto-zoo/test-habitat1'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-treetops',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Treetops',
          nextHabitat: 'Grasslands',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'MW1',
              navigatePath: '/h/endangered-wolf-center/maned-wolves',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/toronto-zoo/sumatran-orangutans',
              devNavigatePath: '/h/toronto-zoo/test-habitat1',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 5,
      section: 'Treetops',
      nextHabitat: 'Grasslands',
    },
  },
  {
    id: 'O4',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "Another special animal species is the Maned Wolf. Let me know whenever you're ready to meet Lucky, a beloved ambassador of the grasslands! Take your time. No rush.",
          buttons: [
            {
              label: 'To the Grasslands',
              navigatePath: '/h/endangered-wolf-center/maned-wolves',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf',
              targetStepId: 'MW1',
            },
          ],
          when: ['/h/toronto-zoo/sumatran-orangutans', '/h/toronto-zoo/test-habitat1'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-treetops',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Treetops',
          nextHabitat: 'Grasslands',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'MW1',
              navigatePath: '/h/endangered-wolf-center/maned-wolves',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/toronto-zoo/sumatran-orangutans',
              devNavigatePath: '/h/toronto-zoo/test-habitat1',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 6,
      section: 'Treetops',
      nextHabitat: 'Grasslands',
    },
  },
  {
    id: 'MW1',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            'Welcome to the Grasslands where the rare South American Maned Wolves lead graceful lives. Let me introduce you to Lucky and her friend Luigi.',
          buttons: [
            {
              label: "What's Lucky's Story?",
              navigatePath: '/h/endangered-wolf-center/maned-wolves?tab=watch&id=675cad840782f32af2c67e30',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf?tab=watch&id=693b3757b02cbc814c5a0a24',
              targetStepId: 'MW2',
            },
          ],
          when: ['/h/endangered-wolf-center/maned-wolves', '/h/lone-pine-koala-sanctuary/wolf'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-grasslands',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Grasslands',
          nextHabitat: 'Playground',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'BB1',
              navigatePath: '/home',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/endangered-wolf-center/maned-wolves',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 7,
      section: 'Grasslands',
      nextHabitat: 'Playground',
    },
  },
  {
    id: 'MW2',
    ui: {
      onMediaClose: {
        targetStepId: 'MW3',
      },
      banner: [
        {
          type: 'guide',
          mascot: 'idle',
          when: ['/h/endangered-wolf-center/maned-wolves', '/h/lone-pine-koala-sanctuary/wolf'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-grasslands',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Grasslands',
          nextHabitat: 'Playground',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'BB1',
              navigatePath: '/home',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/endangered-wolf-center/maned-wolves?tab=watch&id=675cad840782f32af2c67e30',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf?tab=watch&id=693b3757b02cbc814c5a0a24',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 8,
      section: 'Grasslands',
      nextHabitat: 'Playground',
    },
  },
  {
    id: 'MW3',
    ui: {
      autoAdvance: {
        delayMs: 10000,
        targetStepId: 'MW4',
        when: ['/h/endangered-wolf-center/maned-wolves', '/h/lone-pine-koala-sanctuary/wolf'],
      },
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "Maned wolves are so special, they are not a fox nor a wolf. That's why Lucky's breeding program is run across accredited zoos to support the health of her species. What did you think of her story?",
          when: ['/h/endangered-wolf-center/maned-wolves', '/h/lone-pine-koala-sanctuary/wolf'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-grasslands',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Grasslands',
          nextHabitat: 'Playground',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'BB1',
              navigatePath: '/home',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/endangered-wolf-center/maned-wolves',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 9,
      section: 'Grasslands',
      nextHabitat: 'Playground',
    },
  },
  {
    id: 'MW4',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "Today, Lucky and Luigi's love story is still unfolding day by day... Will their breeding program goals be successful? If you ask me, Lucky really likes to play hard to get!",
          buttons: [
            {
              label: 'Show me!',
              navigatePath: '/h/endangered-wolf-center/maned-wolves?tab=watch&card=album&id=69a9ced4e1951ac0d908781b',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf?tab=watch&id=67b794d8b30ee5b1fbfc8fc0',
              targetStepId: 'MW5',
            },
          ],
          when: ['/h/endangered-wolf-center/maned-wolves', '/h/lone-pine-koala-sanctuary/wolf'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-grasslands',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Grasslands',
          nextHabitat: 'Playground',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'BB1',
              navigatePath: '/home',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/endangered-wolf-center/maned-wolves',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 10,
      section: 'Grasslands',
      nextHabitat: 'Playground',
    },
  },
  {
    id: 'MW5',
    ui: {
      onMediaClose: {
        targetStepId: 'MW6',
      },
      banner: [
        {
          type: 'guide',
          mascot: 'idle',
          when: ['/h/endangered-wolf-center/maned-wolves', '/h/lone-pine-koala-sanctuary/wolf'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-grasslands',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Grasslands',
          nextHabitat: 'Playground',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'BB1',
              navigatePath: '/home',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/endangered-wolf-center/maned-wolves?tab=watch&card=album&id=69a9ced4e1951ac0d908781b',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf?tab=watch&id=67b794d8b30ee5b1fbfc8fc0',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 11,
      section: 'Grasslands',
      nextHabitat: 'Playground',
    },
  },
  {
    id: 'MW6',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble: 'Can you tell Lucky and Luigi apart?',
          buttons: [
            {
              label: "What's the Secret?",
              navigatePath: '/h/endangered-wolf-center/maned-wolves?tab=info',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf?tab=info',
              targetStepId: 'MW7',
            },
          ],
          when: ['/h/endangered-wolf-center/maned-wolves', '/h/lone-pine-koala-sanctuary/wolf'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-grasslands',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Grasslands',
          nextHabitat: 'Playground',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'BB1',
              navigatePath: '/home',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/endangered-wolf-center/maned-wolves?tab=info',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf?tab=info',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 12,
      section: 'Grasslands',
      nextHabitat: 'Playground',
    },
  },
  {
    id: 'MW7',
    ui: {
      autoAdvance: {
        delayMs: 8000,
        targetStepId: 'MW8',
        when: ['/h/endangered-wolf-center/maned-wolves', '/h/lone-pine-koala-sanctuary/wolf'],
      },
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "There's a full guide in every habitat. Personally, I think Lucky's ears flop a bit more than Luigi's ears! Can you tell?",
          when: ['/h/endangered-wolf-center/maned-wolves', '/h/lone-pine-koala-sanctuary/wolf'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-grasslands',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Grasslands',
          nextHabitat: 'Playground',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'BB1',
              navigatePath: '/home',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/endangered-wolf-center/maned-wolves',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 13,
      section: 'Grasslands',
      nextHabitat: 'Playground',
    },
  },
  {
    id: 'MW8',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "Breeding programs lead to amazing baby moments and milestones. Want to see my faves as we make our way to our next stop? You name the pace though, let's hang out here more if you'd like!",
          buttons: [
            {
              label: "I'm ready to keep going!",
              navigatePath: '/home',
              targetStepId: 'BB1',
            },
          ],
          when: ['/h/endangered-wolf-center/maned-wolves', '/h/lone-pine-koala-sanctuary/wolf'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-grasslands',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Grasslands',
          nextHabitat: 'Playground',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'BB1',
              navigatePath: '/home',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/endangered-wolf-center/maned-wolves',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 14,
      section: 'Grasslands',
      nextHabitat: 'Playground',
    },
  },
  {
    id: 'BB1',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "We're back on the home page now. Let me show you this playlist of baby lions, baby leopards, and baby monkeys growing up right in front of our eyes.",
          buttons: [
            {
              label: 'Show me the zoolife babies!',
              navigatePath: '/home?playlistId=6970d986d23fc76d59c34a19&mediaId=65cd40afedf5be233aac21bc',
              devNavigatePath: '/home?playlistId=67587b3bde2dc22bdd6b3917&mediaId=69a6ff88bacf02cae5add165',
              targetStepId: 'BB2',
              forceReload: true,
            },
          ],
          when: ['/home', '/home'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-playground',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Playground',
          nextHabitat: 'Mountains',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'SL1',
              navigatePath: '/h/toronto-zoo/snow-leopard',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/home?playlistId=6970d986d23fc76d59c34a19&mediaId=65cd40afedf5be233aac21bc',
              devNavigatePath: '/home?playlistId=67587b3bde2dc22bdd6b3917&mediaId=69a6ff88bacf02cae5add165',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 15,
      section: 'Playground',
      nextHabitat: 'Mountains',
    },
  },
  {
    id: 'BB2',
    ui: {
      onMediaClose: {
        targetStepId: 'BB3',
      },
      banner: [
        {
          type: 'guide',
          mascot: 'idle',
          when: ['/home', '/home'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-playground',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Playground',
          nextHabitat: 'Mountains',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'SL1',
              navigatePath: '/h/toronto-zoo/snow-leopard',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/home?playlistId=6970d986d23fc76d59c34a19&mediaId=65cd40afedf5be233aac21bc',
              devNavigatePath: '/home?playlistId=67587b3bde2dc22bdd6b3917&mediaId=69a6ff88bacf02cae5add165',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 16,
      section: 'Playground',
      nextHabitat: 'Mountains',
    },
  },
  {
    id: 'BB3',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            'On zoolife you can catch both the arrival of newborns and the early weeks & months that follow. Watching their first feedings, friendships... first swims, sprints, and even tumbles! Can you relate?',
          buttons: [
            {
              label: 'I remember when I did that as a baby!',
              targetStepId: 'BB4',
            },
            {
              label: 'No memories from when I was a baby!',
              targetStepId: 'BB4',
            },
          ],
          when: '/home',
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-playground',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Playground',
          nextHabitat: 'Mountains',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'SL1',
              navigatePath: '/h/toronto-zoo/snow-leopard',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/home',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 17,
      section: 'Playground',
      nextHabitat: 'Mountains',
    },
  },
  {
    id: 'BB4',
    ui: {
      autoAdvance: {
        delayMs: 8000,
        targetStepId: 'BB5',
        when: '/home',
      },
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            'Before I forget, if you click on "Follow", you get updates on that animal\'s ongoing story! Okay, we should make our way to meet some of the keepers and care teams behind the scenes. No rush.',
          when: '/home',
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-playground',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Playground',
          nextHabitat: 'Mountains',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'SL1',
              navigatePath: '/h/toronto-zoo/snow-leopard',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/home',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 18,
      section: 'Playground',
      nextHabitat: 'Mountains',
    },
  },
  {
    id: 'BB5',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "Let's wander down the scenic path... It's so worth it! I'll show you some star animals on the way, along with some of my weird and quirky favourites!",
          buttons: [
            {
              label: 'Take me to the Mountains!',
              navigatePath: '/h/toronto-zoo/snow-leopard',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              targetStepId: 'SL1',
            },
          ],
          when: '/home',
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-mountains',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Mountains',
          nextHabitat: 'Rivers',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'RO1',
              navigatePath: '/h/akron-zoo/north-american-river-otters',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/home',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 19,
      section: 'Mountains',
      nextHabitat: 'Rivers',
    },
  },
  {
    id: 'SL1',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            'Welcome to the Mountains! For some animals like snow leopards, we have not one, but two habitats on zoolife! Can you guess why?',
          buttons: [
            {
              label: 'To see them in different time zones',
              targetStepId: 'SL2',
            },
            {
              label: 'Because they are notoriously good at hiding',
              targetStepId: 'SL2',
            },
          ],
          when: ['/h/toronto-zoo/snow-leopard', '/h/lone-pine-koala-sanctuary/koalas'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-mountains',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Mountains',
          nextHabitat: 'Rivers',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'RO1',
              navigatePath: '/h/akron-zoo/north-american-river-otters',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/toronto-zoo/snow-leopard',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 20,
      section: 'Mountains',
      nextHabitat: 'Rivers',
    },
  },
  {
    id: 'SL2',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "Both are correct! Zoolife livestreams from multiple camera angles, and from different time zones around the world. If they're not visible now on the live cameras, catch up on recent video sightings!",
          buttons: [
            {
              label: "Let's see some whiskers!",
              navigatePath: '/h/toronto-zoo/snow-leopard?tab=watch&card=album&id=69949b4d0702216d4e537ba5',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas?tab=watch&card=album&id=65ae9da40d2611b066000f0e',
              targetStepId: 'SL3',
            },
          ],
          when: ['/h/toronto-zoo/snow-leopard', '/h/lone-pine-koala-sanctuary/koalas'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-mountains',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Mountains',
          nextHabitat: 'Rivers',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'RO1',
              navigatePath: '/h/akron-zoo/north-american-river-otters',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/toronto-zoo/snow-leopard',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 21,
      section: 'Mountains',
      nextHabitat: 'Rivers',
    },
  },
  {
    id: 'SL3',
    ui: {
      onMediaClose: {
        targetStepId: 'SL4',
      },
      banner: [
        {
          type: 'guide',
          mascot: 'idle',
          when: ['/h/toronto-zoo/snow-leopard', '/h/lone-pine-koala-sanctuary/koalas'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-mountains',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Mountains',
          nextHabitat: 'Rivers',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'RO1',
              navigatePath: '/h/akron-zoo/north-american-river-otters',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/toronto-zoo/snow-leopard?tab=watch&card=album&id=69949b4d0702216d4e537ba5',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas?tab=watch&card=album&id=65ae9da40d2611b066000f0e',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 22,
      section: 'Mountains',
      nextHabitat: 'Rivers',
    },
  },
  {
    id: 'SL4',
    ui: {
      autoAdvance: {
        delayMs: 15000,
        targetStepId: 'SL5',
        when: ['/h/toronto-zoo/snow-leopard', '/h/lone-pine-koala-sanctuary/koalas'],
      },
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "There's something about watching animals move at their own pace that makes everything else feel a little quieter... What do you think?",
          buttons: [
            {
              label: 'I feel relaxed',
              targetStepId: 'SL5',
            },
          ],
          when: ['/h/toronto-zoo/snow-leopard', '/h/lone-pine-koala-sanctuary/koalas'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-mountains',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Mountains',
          nextHabitat: 'Rivers',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'RO1',
              navigatePath: '/h/akron-zoo/north-american-river-otters',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/toronto-zoo/snow-leopard',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 23,
      section: 'Mountains',
      nextHabitat: 'Rivers',
    },
  },
  {
    id: 'SL5',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            'Snow leopards are incredible creatures. While there are many "big cats", snow leopards are the most elusive of them all. Why do you think some animals like to hide?',
          buttons: [
            {
              label: "It's an adaptation to their environment",
              targetStepId: 'SL6',
            },
            {
              label: "I don't know, but I can relate...",
              targetStepId: 'SL6',
            },
          ],
          when: ['/h/toronto-zoo/snow-leopard', '/h/lone-pine-koala-sanctuary/koalas'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-mountains',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Mountains',
          nextHabitat: 'Rivers',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'RO1',
              navigatePath: '/h/akron-zoo/north-american-river-otters',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/toronto-zoo/snow-leopard',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 24,
      section: 'Mountains',
      nextHabitat: 'Rivers',
    },
  },
  {
    id: 'SL6',
    ui: {
      autoAdvance: {
        delayMs: 15000,
        targetStepId: 'SL7',
        when: ['/h/toronto-zoo/snow-leopard', '/h/lone-pine-koala-sanctuary/koalas'],
      },
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            'What environmental factors might make us humans want to hide sometimes? Journal down some ideas. Which ones apply to you?',
          buttons: [
            {
              label: "I'm finished answering",
              targetStepId: 'SL7',
            },
          ],
          when: ['/h/toronto-zoo/snow-leopard', '/h/lone-pine-koala-sanctuary/koalas'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-mountains',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Mountains',
          nextHabitat: 'Rivers',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'RO1',
              navigatePath: '/h/akron-zoo/north-american-river-otters',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/toronto-zoo/snow-leopard',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 25,
      section: 'Mountains',
      nextHabitat: 'Rivers',
    },
  },
  {
    id: 'SL7',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "If you're feeling curious, each habitat has a little quiz filled with fun facts! Do you know the common nickname for this elusive species?",
          buttons: [
            {
              label: 'Quiz me!',
              navigatePath: '/h/toronto-zoo/snow-leopard?tab=learn',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas?tab=learn',
              targetStepId: 'SL8',
            },
            {
              label: 'No thanks',
              targetStepId: 'SL9',
            },
          ],
          when: ['/h/toronto-zoo/snow-leopard', '/h/lone-pine-koala-sanctuary/koalas'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-mountains',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Mountains',
          nextHabitat: 'Rivers',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'RO1',
              navigatePath: '/h/akron-zoo/north-american-river-otters',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/toronto-zoo/snow-leopard',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 26,
      section: 'Mountains',
      nextHabitat: 'Rivers',
    },
  },
  {
    id: 'SL8',
    ui: {
      autoAdvance: {
        delayMs: 30000,
        targetStepId: 'SL9',
        when: ['/h/toronto-zoo/snow-leopard', '/h/lone-pine-koala-sanctuary/koalas'],
      },
      banner: [
        {
          type: 'guide',
          mascot: 'idle',
          when: ['/h/toronto-zoo/snow-leopard', '/h/lone-pine-koala-sanctuary/koalas'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-mountains',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Mountains',
          nextHabitat: 'Rivers',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'RO1',
              navigatePath: '/h/akron-zoo/north-american-river-otters',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/toronto-zoo/snow-leopard?tab=learn',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas?tab=learn',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 27,
      section: 'Mountains',
      nextHabitat: 'Rivers',
    },
  },
  {
    id: 'SL9',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            'This next animal coming up loves hanging out by the water! Let me know when you\'re ready. Stay with the "snowballs" as long as you like.',
          buttons: [
            {
              label: 'Come down to the river',
              navigatePath: '/h/akron-zoo/north-american-river-otters',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              targetStepId: 'RO1',
            },
          ],
          when: ['/h/toronto-zoo/snow-leopard', '/h/lone-pine-koala-sanctuary/koalas'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-rivers',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Rivers',
          nextHabitat: 'Forest',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'B1',
              navigatePath: '/h/great-plains-zoo/coastal-brown-bears',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/toronto-zoo/snow-leopard',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 28,
      section: 'Rivers',
      nextHabitat: 'Forest',
    },
  },
  {
    id: 'RO1',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "Otters spend most of their day in motion — weaving through water, diving, and sliding along the banks. I find it's one of our most playful habitats on zoolife.",
          buttons: [
            {
              label: "Let's see them swim!",
              navigatePath: '/h/akron-zoo/north-american-river-otters?tab=watch&id=6999ca83e1951ac0d966abb7',
              devNavigatePath:
                '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears?tab=watch&id=68b0776218c3b5b9afce6f74',
              targetStepId: 'RO2',
            },
          ],
          when: [
            '/h/akron-zoo/north-american-river-otters',
            '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
          ],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-rivers',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Rivers',
          nextHabitat: 'Forest',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'B1',
              navigatePath: '/h/great-plains-zoo/coastal-brown-bears',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/akron-zoo/north-american-river-otters',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 29,
      section: 'Rivers',
      nextHabitat: 'Forest',
    },
  },
  {
    id: 'RO2',
    ui: {
      onMediaClose: {
        targetStepId: 'RO3',
      },
      banner: [
        {
          type: 'guide',
          mascot: 'idle',
          when: [
            '/h/akron-zoo/north-american-river-otters',
            '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
          ],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-rivers',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Rivers',
          nextHabitat: 'Forest',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'B1',
              navigatePath: '/h/great-plains-zoo/coastal-brown-bears',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/akron-zoo/north-american-river-otters?tab=watch&id=6999ca83e1951ac0d966abb7',
              devNavigatePath:
                '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears?tab=watch&id=68b0776218c3b5b9afce6f74',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 30,
      section: 'Rivers',
      nextHabitat: 'Forest',
    },
  },
  {
    id: 'RO3',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            'If you could go take a swim right now, would you pick a river, or a pond? Because our next animal loves a good puddle...',
          buttons: [
            {
              label: 'River (shallow)',
              targetStepId: 'RO4',
            },
            {
              label: 'Pond (deep)',
              targetStepId: 'RO4',
            },
          ],
          when: [
            '/h/akron-zoo/north-american-river-otters',
            '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
          ],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-rivers',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Rivers',
          nextHabitat: 'Forest',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'B1',
              navigatePath: '/h/great-plains-zoo/coastal-brown-bears',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/akron-zoo/north-american-river-otters',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 31,
      section: 'Rivers',
      nextHabitat: 'Forest',
    },
  },
  {
    id: 'RO4',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "Let's slow down and hang out with the otters for a little longer. When you're ready, we can head over to see the big puddle splashers.",
          buttons: [
            {
              label: 'On to the forests',
              navigatePath: '/h/great-plains-zoo/coastal-brown-bears',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              targetStepId: 'B1',
            },
          ],
          when: [
            '/h/akron-zoo/north-american-river-otters',
            '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
          ],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-rivers',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Rivers',
          nextHabitat: 'Forest',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'B1',
              navigatePath: '/h/great-plains-zoo/coastal-brown-bears',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/akron-zoo/north-american-river-otters',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 32,
      section: 'Rivers',
      nextHabitat: 'Forest',
    },
  },
  {
    id: 'B1',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            'Bears have a rhythm all their own. Sometimes they roam, sometimes they splash, and sometimes they simply rest in the sun.',
          buttons: [
            {
              label: 'Show me a splash!',
              navigatePath:
                '/h/great-plains-zoo/coastal-brown-bears?tab=watch&card=album&search=splash&id=68dd4705cb9617ee92cba9c4',
              devNavigatePath:
                '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears?tab=watch&id=68b0776218c3b5b9afce6f74',
              targetStepId: 'B2',
            },
          ],
          when: [
            '/h/great-plains-zoo/coastal-brown-bears',
            '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
          ],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-forest',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Forest',
          nextHabitat: 'Skies',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'CC1',
              navigatePath: '/h/butterfly-pavilion/chrysalis-chamber',
              devNavigatePath: '/h/zoolife-office/office',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/great-plains-zoo/coastal-brown-bears',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 33,
      section: 'Forest',
      nextHabitat: 'Skies',
    },
  },
  {
    id: 'B2',
    ui: {
      onMediaClose: {
        targetStepId: 'B3',
      },
      banner: [
        {
          type: 'guide',
          mascot: 'idle',
          when: [
            '/h/great-plains-zoo/coastal-brown-bears',
            '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
          ],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-forest',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Forest',
          nextHabitat: 'Skies',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'CC1',
              navigatePath: '/h/butterfly-pavilion/chrysalis-chamber',
              devNavigatePath: '/h/zoolife-office/office',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath:
                '/h/great-plains-zoo/coastal-brown-bears?tab=watch&card=album&search=splash&id=68dd4705cb9617ee92cba9c4',
              devNavigatePath:
                '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears?tab=watch&id=68b0776218c3b5b9afce6f74',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 34,
      section: 'Forest',
      nextHabitat: 'Skies',
    },
  },
  {
    id: 'B3',
    ui: {
      autoAdvance: {
        delayMs: 20000,
        targetStepId: 'B4',
        when: ['/h/great-plains-zoo/coastal-brown-bears', '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears'],
      },
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "Watching them relax reminds me it's important to slow down when things are moving too fast... Breathe in slowly, then try letting out a big sigh.",
          when: [
            '/h/great-plains-zoo/coastal-brown-bears',
            '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
          ],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-forest',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Forest',
          nextHabitat: 'Skies',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'CC1',
              navigatePath: '/h/butterfly-pavilion/chrysalis-chamber',
              devNavigatePath: '/h/zoolife-office/office',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/great-plains-zoo/coastal-brown-bears',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 35,
      section: 'Forest',
      nextHabitat: 'Skies',
    },
  },
  {
    id: 'B4',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble: "Ok, let's get a little weirder. Are you ready for something different?",
          buttons: [
            {
              label: "Let's flutter over!",
              navigatePath: '/h/butterfly-pavilion/chrysalis-chamber',
              devNavigatePath: '/h/zoolife-office/office',
              targetStepId: 'CC1',
            },
            {
              label: 'Hmmm...',
              navigatePath: '/h/butterfly-pavilion/chrysalis-chamber',
              devNavigatePath: '/h/zoolife-office/office',
              targetStepId: 'CC1',
            },
          ],
          when: [
            '/h/great-plains-zoo/coastal-brown-bears',
            '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
          ],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-forest',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Forest',
          nextHabitat: 'Skies',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'CC1',
              navigatePath: '/h/butterfly-pavilion/chrysalis-chamber',
              devNavigatePath: '/h/zoolife-office/office',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/great-plains-zoo/coastal-brown-bears',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 36,
      section: 'Forest',
      nextHabitat: 'Skies',
    },
  },
  {
    id: 'CC1',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            'Inside these chrysalises, also called cocoons, butterflies are transforming! If the timing is right, we might even see one emerge.',
          buttons: [
            {
              label: 'Show me!',
              navigatePath: '/h/butterfly-pavilion/chrysalis-chamber?tab=watch&id=6828ada919ce5503dc9b18d9',
              devNavigatePath: '/h/zoolife-office/office?tab=watch&card=album&id=69a8d533b496f33c58b76cf5',
              targetStepId: 'CC2',
            },
          ],
          when: ['/h/butterfly-pavilion/chrysalis-chamber', '/h/zoolife-office/office'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-skies',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Skies',
          nextHabitat: 'The Undergrowth',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'ANT1',
              navigatePath: '/h/butterfly-pavilion/leafcutter-ants',
              devNavigatePath: '/h/zoolife-office/office',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/butterfly-pavilion/chrysalis-chamber',
              devNavigatePath: '/h/zoolife-office/office',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 37,
      section: 'Skies',
      nextHabitat: 'The Undergrowth',
    },
  },
  {
    id: 'CC2',
    ui: {
      onMediaClose: {
        targetStepId: 'CC3',
      },
      banner: [
        {
          type: 'guide',
          mascot: 'idle',
          when: ['/h/butterfly-pavilion/chrysalis-chamber', '/h/zoolife-office/office'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-skies',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Skies',
          nextHabitat: 'The Undergrowth',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'ANT1',
              navigatePath: '/h/butterfly-pavilion/leafcutter-ants',
              devNavigatePath: '/h/zoolife-office/office',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/butterfly-pavilion/chrysalis-chamber?tab=watch&id=6828ada919ce5503dc9b18d9',
              devNavigatePath: '/h/zoolife-office/office?tab=watch&card=album&id=69a8d533b496f33c58b76cf5',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 38,
      section: 'Skies',
      nextHabitat: 'The Undergrowth',
    },
  },
  {
    id: 'CC3',
    ui: {
      autoAdvance: {
        delayMs: 10000,
        targetStepId: 'CC4',
        when: ['/h/butterfly-pavilion/chrysalis-chamber', '/h/zoolife-office/office'],
      },
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "The chrysalis chamber based out of the butterfly pavilion. How unique was that? We're seeing the magic of nature in real time!",
          when: ['/h/butterfly-pavilion/chrysalis-chamber', '/h/zoolife-office/office'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-skies',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Skies',
          nextHabitat: 'The Undergrowth',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'ANT1',
              navigatePath: '/h/butterfly-pavilion/leafcutter-ants',
              devNavigatePath: '/h/zoolife-office/office',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/butterfly-pavilion/chrysalis-chamber',
              devNavigatePath: '/h/zoolife-office/office',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 39,
      section: 'Skies',
      nextHabitat: 'The Undergrowth',
    },
  },
  {
    id: 'CC4',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            'For mammals, more private care is often involved, but here we can see butterflies unfurl their wings for the first time.',
          buttons: [
            {
              label: 'Fascinating!',
              targetStepId: 'CC5',
            },
            {
              label: 'Not for me',
              targetStepId: 'CC6',
            },
          ],
          when: ['/h/butterfly-pavilion/chrysalis-chamber', '/h/zoolife-office/office'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-skies',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Skies',
          nextHabitat: 'The Undergrowth',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'ANT1',
              navigatePath: '/h/butterfly-pavilion/leafcutter-ants',
              devNavigatePath: '/h/zoolife-office/office',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/butterfly-pavilion/chrysalis-chamber',
              devNavigatePath: '/h/zoolife-office/office',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 40,
      section: 'Skies',
      nextHabitat: 'The Undergrowth',
    },
  },
  {
    id: 'CC5',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble: 'You and me both! Since you thought that was cool, let me take you to another hidden gem...',
          buttons: [
            {
              label: 'Show me!',
              navigatePath: '/h/butterfly-pavilion/leafcutter-ants',
              devNavigatePath: '/h/zoolife-office/office',
              targetStepId: 'ANT1',
            },
          ],
          when: ['/h/butterfly-pavilion/chrysalis-chamber', '/h/zoolife-office/office'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-skies',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Skies',
          nextHabitat: 'The Undergrowth',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'ANT1',
              navigatePath: '/h/butterfly-pavilion/leafcutter-ants',
              devNavigatePath: '/h/zoolife-office/office',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/butterfly-pavilion/chrysalis-chamber',
              devNavigatePath: '/h/zoolife-office/office',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 41,
      section: 'Skies',
      nextHabitat: 'The Undergrowth',
    },
  },
  {
    id: 'CC6',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "That's cool, not everyone likes insects. We do have some other small yet mighty bugs! Would you like to check them out, or head to the Bayou instead?",
          buttons: [
            {
              label: "Let's see some bugs!",
              navigatePath: '/h/butterfly-pavilion/leafcutter-ants',
              devNavigatePath: '/h/zoolife-office/office',
              targetStepId: 'ANT1',
            },
            {
              label: 'Take me to the Bayou',
              navigatePath: '/h/phoenix-herpetological-sanctuary/american-alligators',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              targetStepId: 'ALL1',
            },
          ],
          when: ['/h/butterfly-pavilion/chrysalis-chamber', '/h/zoolife-office/office'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-skies',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Skies',
          nextHabitat: 'The Undergrowth',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'ANT1',
              navigatePath: '/h/butterfly-pavilion/leafcutter-ants',
              devNavigatePath: '/h/zoolife-office/office',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/butterfly-pavilion/chrysalis-chamber',
              devNavigatePath: '/h/zoolife-office/office',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 42,
      section: 'Skies',
      nextHabitat: 'The Undergrowth',
    },
  },
  {
    id: 'ANT1',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "These ants are always on a mission. Let's follow one carrying a leaf many times its size back to its colony.",
          buttons: [
            {
              label: 'Show me!',
              navigatePath: '/h/butterfly-pavilion/leafcutter-ants?tab=watch&id=697e6eb70702216d4e178345',
              devNavigatePath: '/h/zoolife-office/office?tab=watch&card=album&id=69a8d50fbaa80ed0cb07235a',
              targetStepId: 'ANT2',
            },
          ],
          when: ['/h/butterfly-pavilion/leafcutter-ants', '/h/zoolife-office/office'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-the-undergrowth',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'The Undergrowth',
          nextHabitat: 'Bayou',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'ALL1',
              navigatePath: '/h/phoenix-herpetological-sanctuary/american-alligators',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/butterfly-pavilion/leafcutter-ants',
              devNavigatePath: '/h/zoolife-office/office',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 43,
      section: 'The Undergrowth',
      nextHabitat: 'Bayou',
    },
  },
  {
    id: 'ANT2',
    ui: {
      onMediaClose: {
        targetStepId: 'ANT3',
      },
      banner: [
        {
          type: 'guide',
          mascot: 'idle',
          when: ['/h/butterfly-pavilion/leafcutter-ants', '/h/zoolife-office/office'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-the-undergrowth',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'The Undergrowth',
          nextHabitat: 'Bayou',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'ALL1',
              navigatePath: '/h/phoenix-herpetological-sanctuary/american-alligators',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/butterfly-pavilion/leafcutter-ants?tab=watch&id=697e6eb70702216d4e178345',
              devNavigatePath: '/h/zoolife-office/office?tab=watch&card=album&id=69a8d50fbaa80ed0cb07235a',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 44,
      section: 'The Undergrowth',
      nextHabitat: 'Bayou',
    },
  },
  {
    id: 'ANT3',
    ui: {
      autoAdvance: {
        delayMs: 10000,
        targetStepId: 'ANT4',
        when: ['/h/butterfly-pavilion/leafcutter-ants', '/h/zoolife-office/office'],
      },
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            'Ants remind me of the power of intention. They are so dedicated and present for their team goals. In their case... chomping up some leaves to bring home!',
          when: ['/h/butterfly-pavilion/leafcutter-ants', '/h/zoolife-office/office'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-the-undergrowth',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'The Undergrowth',
          nextHabitat: 'Bayou',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'ALL1',
              navigatePath: '/h/phoenix-herpetological-sanctuary/american-alligators',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/butterfly-pavilion/leafcutter-ants',
              devNavigatePath: '/h/zoolife-office/office',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 45,
      section: 'The Undergrowth',
      nextHabitat: 'Bayou',
    },
  },
  {
    id: 'ANT4',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble: 'Ready for our last pit-stop before we meet the keepers and caretakers behind the scenes?',
          buttons: [
            {
              label: 'To the Bayou',
              navigatePath: '/h/phoenix-herpetological-sanctuary/american-alligators',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              targetStepId: 'ALL1',
            },
          ],
          when: ['/h/butterfly-pavilion/leafcutter-ants', '/h/zoolife-office/office'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-the-undergrowth',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'The Undergrowth',
          nextHabitat: 'Bayou',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'ALL1',
              navigatePath: '/h/phoenix-herpetological-sanctuary/american-alligators',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/butterfly-pavilion/leafcutter-ants',
              devNavigatePath: '/h/zoolife-office/office',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 46,
      section: 'The Undergrowth',
      nextHabitat: 'Bayou',
    },
  },
  {
    id: 'ALL1',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble: 'This habitat moves at a slower pace. Reptiles spend a LOT of time soaking up warmth from the sun.',
          buttons: [
            {
              label: 'Show me!',
              navigatePath: '/h/phoenix-herpetological-sanctuary/american-alligators?tab=watch&id=668d408d5dbe678c23fecdf2',
              devNavigatePath:
                '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears?tab=watch&id=68b0776218c3b5b9afce6f74',
              targetStepId: 'ALL2',
            },
          ],
          when: ['/h/phoenix-herpetological-sanctuary/american-alligators', '/h/lone-pine-koala-sanctuary/koalas'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-bayou',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Bayou',
          nextHabitat: 'Pacific Coast',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'P1',
              navigatePath: '/h/santa-barbara/humboldt-penguin',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/phoenix-herpetological-sanctuary/american-alligators',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 47,
      section: 'Bayou',
      nextHabitat: 'Pacific Coast',
    },
  },
  {
    id: 'ALL2',
    ui: {
      onMediaClose: {
        targetStepId: 'ALL3',
      },
      banner: [
        {
          type: 'guide',
          mascot: 'idle',
          when: [
            '/h/phoenix-herpetological-sanctuary/american-alligators',
            '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
          ],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-bayou',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Bayou',
          nextHabitat: 'Pacific Coast',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'P1',
              navigatePath: '/h/santa-barbara/humboldt-penguin',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/phoenix-herpetological-sanctuary/american-alligators?tab=watch&id=668d408d5dbe678c23fecdf2',
              devNavigatePath:
                '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears?tab=watch&id=68b0776218c3b5b9afce6f74',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 48,
      section: 'Bayou',
      nextHabitat: 'Pacific Coast',
    },
  },
  {
    id: 'ALL3',
    ui: {
      autoAdvance: {
        delayMs: 10000,
        targetStepId: 'ALL4',
        when: [
          '/h/phoenix-herpetological-sanctuary/american-alligators',
          '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
        ],
      },
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            'These reptiles really know how to relax. What bliss! Since they are cool-blooded, they can sit in the sun for so much longer than humans.',
          when: [
            '/h/phoenix-herpetological-sanctuary/american-alligators',
            '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
          ],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-bayou',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Bayou',
          nextHabitat: 'Pacific Coast',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'P1',
              navigatePath: '/h/santa-barbara/humboldt-penguin',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/phoenix-herpetological-sanctuary/american-alligators',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 49,
      section: 'Bayou',
      nextHabitat: 'Pacific Coast',
    },
  },
  {
    id: 'ALL4',
    ui: {
      autoAdvance: {
        delayMs: 10000,
        targetStepId: 'ALL5',
        when: [
          '/h/phoenix-herpetological-sanctuary/american-alligators',
          '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
        ],
      },
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            'Alligators are one of the most impressive reptiles. If you had an afternoon to relax on a sunny day, would you be in the sun like the alligator or in the pool like a bear?',
          buttons: [
            {
              label: 'In the sun',
              targetStepId: 'ALL5',
            },
            {
              label: 'In the pool',
              targetStepId: 'ALL5',
            },
          ],
          when: [
            '/h/phoenix-herpetological-sanctuary/american-alligators',
            '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
          ],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-bayou',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Bayou',
          nextHabitat: 'Pacific Coast',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'P1',
              navigatePath: '/h/santa-barbara/humboldt-penguin',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/phoenix-herpetological-sanctuary/american-alligators',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 50,
      section: 'Bayou',
      nextHabitat: 'Pacific Coast',
    },
  },
  {
    id: 'ALL5',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            'I\'m a sloth, and they say we\'re the "goldilocks" animals when it comes to temperature! 😇  Ready to see how keepers take care of their animals? This next animal loves fish.',
          buttons: [
            {
              label: 'Take me to the Pacific Coast',
              navigatePath: '/h/santa-barbara/humboldt-penguin',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              targetStepId: 'P1',
            },
          ],
          when: [
            '/h/phoenix-herpetological-sanctuary/american-alligators',
            '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
          ],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-bayou',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Bayou',
          nextHabitat: 'Pacific Coast',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'P1',
              navigatePath: '/h/santa-barbara/humboldt-penguin',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/phoenix-herpetological-sanctuary/american-alligators',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 51,
      section: 'Bayou',
      nextHabitat: 'Pacific Coast',
    },
  },
  {
    id: 'P1',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            'Here on the Pacific Coast, the zoo staff and keepers make sure these penguins have all the food, toys, and shelter they can enjoy.',
          buttons: [
            {
              label: 'Replay a Keeper Chat',
              navigatePath: '/h/santa-barbara/humboldt-penguin?tab=watch&card=talks&id=6992cd06d9ad206d3b911aeb',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf?tab=watch&id=67b794d8b30ee5b1fbfc8fc0',
              targetStepId: 'P2',
            },
          ],
          when: ['/h/santa-barbara/humboldt-penguin', '/h/lone-pine-koala-sanctuary/koalas'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-pacific-coast',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Pacific Coast',
          nextHabitat: 'Behind the Scenes',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'V1',
              navigatePath: '/home',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/santa-barbara/humboldt-penguin',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 52,
      section: 'Pacific Coast',
      nextHabitat: 'Behind the Scenes',
    },
  },
  {
    id: 'P2',
    ui: {
      onMediaClose: {
        targetStepId: 'P3',
      },
      banner: [
        {
          type: 'guide',
          mascot: 'idle',
          when: ['/h/santa-barbara/humboldt-penguin', '/h/lone-pine-koala-sanctuary/wolf'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-pacific-coast',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Pacific Coast',
          nextHabitat: 'Behind the Scenes',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'V1',
              navigatePath: '/home',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/santa-barbara/humboldt-penguin?tab=watch&card=talks&id=6992cd06d9ad206d3b911aeb',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf?tab=watch&id=67b794d8b30ee5b1fbfc8fc0',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 53,
      section: 'Pacific Coast',
      nextHabitat: 'Behind the Scenes',
    },
  },
  {
    id: 'P3',
    ui: {
      autoAdvance: {
        delayMs: 15000,
        targetStepId: 'P4',
        when: ['/h/santa-barbara/humboldt-penguin', '/h/lone-pine-koala-sanctuary/wolf'],
      },
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            'Each habitat has a dedicated team taking care of the animals. Each keeper has different responsibilities based on their expertise and the animal. Leaving puzzles, games, scent trails...',
          buttons: [
            {
              label: 'What a cool job!',
              targetStepId: 'P4',
            },
            {
              label: 'That sounds like hard work!',
              targetStepId: 'P4',
            },
          ],
          when: ['/h/santa-barbara/humboldt-penguin', '/h/lone-pine-koala-sanctuary/wolf'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-pacific-coast',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Pacific Coast',
          nextHabitat: 'Behind the Scenes',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'V1',
              navigatePath: '/home',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/santa-barbara/humboldt-penguin',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 54,
      section: 'Pacific Coast',
      nextHabitat: 'Behind the Scenes',
    },
  },
  {
    id: 'P4',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "They also join us directly on zoolife with their latest animal news, right from the scene. I'll show you on our way to our last stop, whenever you're ready",
          buttons: [
            {
              label: 'How do I Join a Keeper Talk?',
              navigatePath: '/home',
              targetStepId: 'V1',
            },
          ],
          when: ['/h/santa-barbara/humboldt-penguin', '/h/lone-pine-koala-sanctuary/wolf'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-pacific-coast',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Pacific Coast',
          nextHabitat: 'Behind the Scenes',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'V1',
              navigatePath: '/home',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/santa-barbara/humboldt-penguin',
              devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 55,
      section: 'Pacific Coast',
      nextHabitat: 'Behind the Scenes',
    },
  },
  {
    id: 'V1',
    ui: {
      autoAdvance: {
        delayMs: 15000,
        targetStepId: 'V2',
        when: '/home',
      },
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "On the right, you'll see Upcoming Events. Click on the 🔔 to be reminded to join to ask keepers questions directly, or simply enjoy these laid-back chats as a zoolife member!",
          when: '/home',
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-behind-the-scenes',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Behind the Scenes',
          nextHabitat: 'Ocean',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'D1',
              navigatePath: '/h/clearwater-marine-aquarium/dolphins',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/home',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 56,
      section: 'Behind the Scenes',
      nextHabitat: 'Ocean',
    },
  },
  {
    id: 'V2',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            'Keep checking back on zoolife because some keepers will bring us LIVE behind the scenes for big feedings or routine vet appointments.',
          buttons: [
            {
              label: 'Show Me!',
              navigatePath: '/h/safari-west/reticulated-giraffe?tab=watch&id=680f2f8d6dd68e9c42a9ac5c',
              devNavigatePath: '/h/toronto-zoo/test-habitat1?tab=watch&card=album&id=6985eb010594f4cb5c929eef',
              targetStepId: 'V3',
            },
          ],
          when: '/home',
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-behind-the-scenes',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Behind the Scenes',
          nextHabitat: 'Ocean',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'D1',
              navigatePath: '/h/clearwater-marine-aquarium/dolphins',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/home',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 57,
      section: 'Behind the Scenes',
      nextHabitat: 'Ocean',
    },
  },
  {
    id: 'V3',
    ui: {
      onMediaClose: {
        targetStepId: 'V4',
      },
      banner: [
        {
          type: 'guide',
          mascot: 'idle',
          when: ['/h/safari-west/reticulated-giraffe', '/h/toronto-zoo/test-habitat1'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-behind-the-scenes',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Behind the Scenes',
          nextHabitat: 'Ocean',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'D1',
              navigatePath: '/h/clearwater-marine-aquarium/dolphins',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/safari-west/reticulated-giraffe?tab=watch&id=680f2f8d6dd68e9c42a9ac5c',
              devNavigatePath: '/h/toronto-zoo/test-habitat1?tab=watch&card=album&id=6985eb010594f4cb5c929eef',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 58,
      section: 'Behind the Scenes',
      nextHabitat: 'Ocean',
    },
  },
  {
    id: 'V4',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble: "Alright, one more stop before we reach the end of our tour. Let's catch a wave!",
          buttons: [
            {
              label: 'Take me to the Ocean',
              navigatePath: '/h/clearwater-marine-aquarium/dolphins',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              targetStepId: 'D1',
            },
          ],
          when: ['/h/safari-west/reticulated-giraffe', '/h/toronto-zoo/test-habitat1'],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-behind-the-scenes',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Behind the Scenes',
          nextHabitat: 'Ocean',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'D1',
              navigatePath: '/h/clearwater-marine-aquarium/dolphins',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/safari-west/reticulated-giraffe',
              devNavigatePath: '/h/toronto-zoo/test-habitat1',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 59,
      section: 'Behind the Scenes',
      nextHabitat: 'Ocean',
    },
  },
  {
    id: 'D1',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble: 'Say hello to our beautiful dolphins, some of the most playful and graceful animals in the ocean.',
          buttons: [
            {
              label: 'Show Me!',
              navigatePath: '/h/clearwater-marine-aquarium/dolphins?tab=watch&id=69a4a78fed609b37e66df52a',
              devNavigatePath:
                '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears?tab=watch&id=68b0776218c3b5b9afce6f74',
              targetStepId: 'D2',
            },
          ],
          when: [
            '/h/clearwater-marine-aquarium/dolphins',
            '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
          ],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-ocean',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Ocean',
          nextHabitat: 'The End',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'End1',
              navigatePath: '/home',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/clearwater-marine-aquarium/dolphins',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 60,
      section: 'Ocean',
      nextHabitat: 'The End',
    },
  },
  {
    id: 'D2',
    ui: {
      onMediaClose: {
        targetStepId: 'D3',
      },
      banner: [
        {
          type: 'guide',
          mascot: 'idle',
          when: [
            '/h/clearwater-marine-aquarium/dolphins',
            '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
          ],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-ocean',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Ocean',
          nextHabitat: 'The End',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'End1',
              navigatePath: '/home',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/clearwater-marine-aquarium/dolphins?tab=watch&id=69a4a78fed609b37e66df52a',
              devNavigatePath:
                '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears?tab=watch&id=68b0776218c3b5b9afce6f74',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 61,
      section: 'Ocean',
      nextHabitat: 'The End',
    },
  },
  {
    id: 'D3',
    ui: {
      autoAdvance: {
        delayMs: 30000,
        targetStepId: 'D4',
        when: ['/h/clearwater-marine-aquarium/dolphins', '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears'],
      },
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            'Aquatic habitats bring a completely different perspective — their movements are so peaceful! Try switching between the 4 different camera views in this habitat using the green bar.',
          when: [
            '/h/clearwater-marine-aquarium/dolphins',
            '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
          ],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-ocean',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Ocean',
          nextHabitat: 'The End',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'End1',
              navigatePath: '/home',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/clearwater-marine-aquarium/dolphins',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 62,
      section: 'Ocean',
      nextHabitat: 'The End',
    },
  },
  {
    id: 'D4',
    ui: {
      autoAdvance: {
        delayMs: 6000,
        targetStepId: 'D5',
        when: ['/h/clearwater-marine-aquarium/dolphins', '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears'],
      },
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "Have you used the chat yet? If not, say hello or leave a reaction for the dolphin lovers here! You can click on community members' profiles to see what other people are checking out.",
          when: [
            '/h/clearwater-marine-aquarium/dolphins',
            '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
          ],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-ocean',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Ocean',
          nextHabitat: 'The End',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'End1',
              navigatePath: '/home',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/clearwater-marine-aquarium/dolphins',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 63,
      section: 'Ocean',
      nextHabitat: 'The End',
    },
  },
  {
    id: 'D5',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "This tour gives you 24 hours of zoolife access. So there's no rush to leave. Let me take you back to home base when you're ready.",
          buttons: [
            {
              label: "Let's Wind Down",
              navigatePath: '/home',
              targetStepId: 'End1',
            },
          ],
          when: [
            '/h/clearwater-marine-aquarium/dolphins',
            '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
          ],
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-ocean',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Ocean',
          nextHabitat: 'The End',
          buttons: [
            {
              label: 'Skip Ahead to Next Habitat',
              targetStepId: 'End1',
              navigatePath: '/home',
              trackAction: 'skipped-ahead',
            },
            {
              label: 'Explore with Guide',
              navigatePath: '/h/clearwater-marine-aquarium/dolphins',
              devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 64,
      section: 'Ocean',
      nextHabitat: 'The End',
    },
  },
  {
    id: 'End1',
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "Congratulations, explorer! You just wandered through forests, grasslands, mountains, and ponds. Keep exploring for as long as you'd like.",
          buttons: [
            {
              label: "I'm done with my guided journey!",
              targetStepId: 'modal-end',
            },
            {
              label: 'How do I become a zoolife member?',
              navigatePath: '/help?tab=plans',
            },
          ],
          when: '/home',
          backButton: {
            label: 'Go Back a Step',
            goBack: true,
            trackAction: 'went-back',
          },
          breakButton: {
            label: 'Explore On My Own',
            targetStepId: 'paused-tour-end',
            trackAction: 'paused',
          },
        },
        {
          type: 'paused',
          currentSection: 'Tour End',
          buttons: [
            {
              label: 'Explore with Guide',
              navigatePath: '/home',
              trackAction: 'resumed',
            },
          ],
        },
      ],
    },
    analytics: {
      stepNumber: 65,
      section: 'Tour End',
    },
  },
  {
    id: 'modal-end',
    ui: {
      modal: {
        kind: 'feedback',
        nextStepId: 'modal-end-submitted',
        onCloseStepId: 'End2',
      },
    },
    restoreTo: 'End2',
    analytics: {
      stepNumber: 66,
    },
  },
  {
    id: 'modal-end-submitted',
    ui: {
      modal: {
        kind: 'feedback',
        submitted: true,
        nextStepId: 'End2',
      },
    },
    restoreTo: 'End2',
    analytics: {
      stepNumber: 67,
    },
  },
  {
    id: 'paused-departure',
    ui: {
      banner: {
        type: 'paused',
        currentSection: 'Departure',
        nextHabitat: 'Treetops',
        buttons: [
          {
            label: 'Skip Ahead to Next Habitat',
            targetStepId: 'O1',
            navigatePath: '/h/toronto-zoo/sumatran-orangutans?tab=watch&id=699f40177c890cb0e7aec70a',
            devNavigatePath: '/h/toronto-zoo/test-habitat1?tab=watch&card=album&id=6985edd47e9d38372a13fde9',
            trackAction: 'skipped-ahead',
          },
          {
            label: 'Explore with Guide',
            goBack: true,
            trackAction: 'resumed',
          },
        ],
      },
    },
    analytics: {
      stepNumber: -1,
      section: 'Departure',
      nextHabitat: 'Treetops',
    },
  },
  {
    id: 'paused-treetops',
    ui: {
      banner: {
        type: 'paused',
        currentSection: 'Treetops',
        nextHabitat: 'Grasslands',
        buttons: [
          {
            label: 'Skip Ahead to Next Habitat',
            targetStepId: 'MW1',
            navigatePath: '/h/endangered-wolf-center/maned-wolves',
            devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf',
            trackAction: 'skipped-ahead',
          },
          {
            label: 'Explore with Guide',
            goBack: true,
            trackAction: 'resumed',
          },
        ],
      },
    },
    analytics: {
      stepNumber: -1,
      section: 'Treetops',
      nextHabitat: 'Grasslands',
    },
  },
  {
    id: 'paused-grasslands',
    ui: {
      banner: {
        type: 'paused',
        currentSection: 'Grasslands',
        nextHabitat: 'Playground',
        buttons: [
          {
            label: 'Skip Ahead to Next Habitat',
            targetStepId: 'BB1',
            navigatePath: '/home',
            trackAction: 'skipped-ahead',
          },
          {
            label: 'Explore with Guide',
            goBack: true,
            trackAction: 'resumed',
          },
        ],
      },
    },
    analytics: {
      stepNumber: -1,
      section: 'Grasslands',
      nextHabitat: 'Playground',
    },
  },
  {
    id: 'paused-playground',
    ui: {
      banner: {
        type: 'paused',
        currentSection: 'Playground',
        nextHabitat: 'Mountains',
        buttons: [
          {
            label: 'Skip Ahead to Next Habitat',
            targetStepId: 'SL1',
            navigatePath: '/h/toronto-zoo/snow-leopard',
            devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
            trackAction: 'skipped-ahead',
          },
          {
            label: 'Explore with Guide',
            goBack: true,
            trackAction: 'resumed',
          },
        ],
      },
    },
    analytics: {
      stepNumber: -1,
      section: 'Playground',
      nextHabitat: 'Mountains',
    },
  },
  {
    id: 'paused-mountains',
    ui: {
      banner: {
        type: 'paused',
        currentSection: 'Mountains',
        nextHabitat: 'Rivers',
        buttons: [
          {
            label: 'Skip Ahead to Next Habitat',
            targetStepId: 'RO1',
            navigatePath: '/h/akron-zoo/north-american-river-otters',
            devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
            trackAction: 'skipped-ahead',
          },
          {
            label: 'Explore with Guide',
            goBack: true,
            trackAction: 'resumed',
          },
        ],
      },
    },
    analytics: {
      stepNumber: -1,
      section: 'Mountains',
      nextHabitat: 'Rivers',
    },
  },
  {
    id: 'paused-rivers',
    ui: {
      banner: {
        type: 'paused',
        currentSection: 'Rivers',
        nextHabitat: 'Forest',
        buttons: [
          {
            label: 'Skip Ahead to Next Habitat',
            targetStepId: 'B1',
            navigatePath: '/h/great-plains-zoo/coastal-brown-bears',
            devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
            trackAction: 'skipped-ahead',
          },
          {
            label: 'Explore with Guide',
            goBack: true,
            trackAction: 'resumed',
          },
        ],
      },
    },
    analytics: {
      stepNumber: -1,
      section: 'Rivers',
      nextHabitat: 'Forest',
    },
  },
  {
    id: 'paused-forest',
    ui: {
      banner: {
        type: 'paused',
        currentSection: 'Forest',
        nextHabitat: 'Skies',
        buttons: [
          {
            label: 'Skip Ahead to Next Habitat',
            targetStepId: 'CC1',
            navigatePath: '/h/butterfly-pavilion/chrysalis-chamber',
            devNavigatePath: '/h/zoolife-office/office',
            trackAction: 'skipped-ahead',
          },
          {
            label: 'Explore with Guide',
            goBack: true,
            trackAction: 'resumed',
          },
        ],
      },
    },
    analytics: {
      stepNumber: -1,
      section: 'Forest',
      nextHabitat: 'Skies',
    },
  },
  {
    id: 'paused-skies',
    ui: {
      banner: {
        type: 'paused',
        currentSection: 'Skies',
        nextHabitat: 'The Undergrowth',
        buttons: [
          {
            label: 'Skip Ahead to Next Habitat',
            targetStepId: 'ANT1',
            navigatePath: '/h/butterfly-pavilion/leafcutter-ants',
            devNavigatePath: '/h/zoolife-office/office',
            trackAction: 'skipped-ahead',
          },
          {
            label: 'Explore with Guide',
            goBack: true,
            trackAction: 'resumed',
          },
        ],
      },
    },
    analytics: {
      stepNumber: -1,
      section: 'Skies',
      nextHabitat: 'The Undergrowth',
    },
  },
  {
    id: 'paused-the-undergrowth',
    ui: {
      banner: {
        type: 'paused',
        currentSection: 'The Undergrowth',
        nextHabitat: 'Bayou',
        buttons: [
          {
            label: 'Skip Ahead to Next Habitat',
            targetStepId: 'ALL1',
            navigatePath: '/h/phoenix-herpetological-sanctuary/american-alligators',
            devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
            trackAction: 'skipped-ahead',
          },
          {
            label: 'Explore with Guide',
            goBack: true,
            trackAction: 'resumed',
          },
        ],
      },
    },
    analytics: {
      stepNumber: -1,
      section: 'The Undergrowth',
      nextHabitat: 'Bayou',
    },
  },
  {
    id: 'paused-bayou',
    ui: {
      banner: {
        type: 'paused',
        currentSection: 'Bayou',
        nextHabitat: 'Pacific Coast',
        buttons: [
          {
            label: 'Skip Ahead to Next Habitat',
            targetStepId: 'P1',
            navigatePath: '/h/santa-barbara/humboldt-penguin',
            devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
            trackAction: 'skipped-ahead',
          },
          {
            label: 'Explore with Guide',
            goBack: true,
            trackAction: 'resumed',
          },
        ],
      },
    },
    analytics: {
      stepNumber: -1,
      section: 'Bayou',
      nextHabitat: 'Pacific Coast',
    },
  },
  {
    id: 'paused-pacific-coast',
    ui: {
      banner: {
        type: 'paused',
        currentSection: 'Pacific Coast',
        nextHabitat: 'Behind the Scenes',
        buttons: [
          {
            label: 'Skip Ahead to Next Habitat',
            targetStepId: 'V1',
            navigatePath: '/home',
            trackAction: 'skipped-ahead',
          },
          {
            label: 'Explore with Guide',
            goBack: true,
            trackAction: 'resumed',
          },
        ],
      },
    },
    analytics: {
      stepNumber: -1,
      section: 'Pacific Coast',
      nextHabitat: 'Behind the Scenes',
    },
  },
  {
    id: 'paused-behind-the-scenes',
    ui: {
      banner: {
        type: 'paused',
        currentSection: 'Behind the Scenes',
        nextHabitat: 'Ocean',
        buttons: [
          {
            label: 'Skip Ahead to Next Habitat',
            targetStepId: 'D1',
            navigatePath: '/h/clearwater-marine-aquarium/dolphins',
            devNavigatePath: '/h/zoolife-office/%F0%9F%90%BB-kodiak-&-katmai:-the-brown-bears',
            trackAction: 'skipped-ahead',
          },
          {
            label: 'Explore with Guide',
            goBack: true,
            trackAction: 'resumed',
          },
        ],
      },
    },
    analytics: {
      stepNumber: -1,
      section: 'Behind the Scenes',
      nextHabitat: 'Ocean',
    },
  },
  {
    id: 'paused-ocean',
    ui: {
      banner: {
        type: 'paused',
        currentSection: 'Ocean',
        nextHabitat: 'The End',
        buttons: [
          {
            label: 'Skip Ahead to Next Habitat',
            targetStepId: 'End1',
            navigatePath: '/home',
            trackAction: 'skipped-ahead',
          },
          {
            label: 'Explore with Guide',
            goBack: true,
            trackAction: 'resumed',
          },
        ],
      },
    },
    analytics: {
      stepNumber: -1,
      section: 'Ocean',
      nextHabitat: 'The End',
    },
  },
  {
    id: 'paused-tour-end',
    ui: {
      banner: {
        type: 'paused',
        currentSection: 'Tour End',
        buttons: [
          {
            label: 'Explore with Guide',
            goBack: true,
            trackAction: 'resumed',
          },
        ],
      },
    },
    analytics: {
      stepNumber: -1,
      section: 'Tour End',
    },
  },
  {
    id: 'End2',
    ui: {
      banner: {
        type: 'guide',
        mascot: 'talking',
        speechBubble: END2_SPEECH_BUBBLE,
        buttons: [
          {
            label: 'Revisit the animals on my own',
            navigatePath: '/home',
            completeTour: true,
          },
          {
            label: 'Restart the tour again',
            restartTour: true,
            restartTourId: 'guidedJourney',
          },
        ],
      },
    },
    analytics: {
      stepNumber: 82,
    },
  },
];

export const guidedJourneyConfig: TourConfig = {
  id: GUIDED_JOURNEY_ID,
  steps: guidedJourneySteps,
};
